import { Flex, Switch, Text } from '@chakra-ui/react';
import { AccessLinkReadMetadata } from '@llx/common';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDistanceToNow, isBefore } from 'date-fns';

import { AccessLinkFieldsFragment, AccessLinkTypes } from '@/@generated/codegen/API';
import CopyButton from '@/ui/CopyButton/CopyButton';
import { getAccessLinkUrl } from '@/utils/helpers/getAccessLinkUrl';
import { parseDate } from '@/utils/helpers/parseDate';

export type AccessLinkListRowType = AccessLinkFieldsFragment;

const columnHelper = createColumnHelper<AccessLinkListRowType>();

type Props = {
  type: AccessLinkTypes;
  onEnableClick: (id: string, isEnabled: boolean) => void;
};

export const getColumns = ({ type, onEnableClick }: Props) => {
  const shouldShowLinkFirstAccountColumn = type === AccessLinkTypes.MasterclassReadAccess;
  const shouldShowForcedUsernameColumn = type === AccessLinkTypes.MasterclassReadAccess;
  const shouldShowIsDemoColumn = type === AccessLinkTypes.MasterclassReadAccess;

  const columns = [
    columnHelper.accessor('isEnabled', {
      header: 'Is enabled',
      enableSorting: false,
      size: 100,
      cell: (props) => {
        return (
          <Switch
            isChecked={props.getValue()}
            onChange={(e) => {
              onEnableClick(props.row.original.id, e.target.checked);
            }}
          />
        );
      },
    }),
    columnHelper.accessor('accessCode', {
      header: 'Access code',
      enableSorting: false,
      size: 170,
      cell: (props) => {
        const code = props.getValue();
        return (
          <Flex alignItems="center" gap={2}>
            <CopyButton text={getAccessLinkUrl(code)} size="xs" />
            <Text>{code}</Text>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created at',
      cell: (props) => {
        const value = props.getValue();
        return <Text>{parseDate(new Date(value).toString())}</Text>;
      },
    }),
    shouldShowIsDemoColumn
      ? columnHelper.display({
          id: 'isDemo',
          header: 'Is Demo',
          size: 75,
          cell: (props) => {
            const metadata = props.row.original.metadata as AccessLinkReadMetadata;
            return <Text>{metadata.isDemo ? 'Yes' : 'No'}</Text>;
          },
        })
      : null,
    columnHelper.accessor('validUntil', {
      header: 'Expires in',
      cell: (props) => {
        const value = props.getValue();
        const expiresIn = formatDistanceToNow(new Date(value), { addSuffix: true });

        if (isBefore(new Date(value), new Date())) {
          return <Text>Expired</Text>;
        }
        return <Text>{expiresIn}</Text>;
      },
    }),
    shouldShowLinkFirstAccountColumn
      ? columnHelper.accessor('linkFirstAccount', {
          header: 'Should link account',
          size: 200,
          cell: (props) => {
            const value = props.getValue();
            const metadata = props.row.original.metadata as AccessLinkReadMetadata;

            return (
              <Text>
                {value ? `Yes ${metadata.linkedUserId ? `(${metadata.linkedUserId})` : ''}` : 'No'}
              </Text>
            );
            // return (<Switch checked={value} isDisabled={true} />)
          },
        })
      : null,
    shouldShowForcedUsernameColumn
      ? columnHelper.display({
          id: 'forcedUsername',
          header: 'Forced username',
          cell: (props) => {
            const metadata = props.row.original.metadata as AccessLinkReadMetadata;
            const forcedUsername = metadata.forcedUsername;

            if (!forcedUsername) return <Text>--</Text>;
            return <Text>{forcedUsername}</Text>;
          },
        })
      : null,
    // columnHelper.accessor('usageCountLeft', {
    //   header: 'Usage count left',
    //   size: 230,
    //   cell: (props) => <Text>{props.getValue()}</Text>,
    // }),
  ];

  return columns.filter((c) => !!c);
};
